import { useState, useMemo } from 'react';

import { $getSelection, $isRangeSelection, LexicalEditor } from 'lexical';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';

import { useValidationService } from '../../../../../services/hooks';
import { Dialog, DialogContent } from '../../../../../Dialog';
import { Button } from '../../../../../Button';
import { TextInput } from '../../../../../input';
import { sanitizeUrl } from '../../../utils';
import { useForm } from '../../hooks';

interface LinkDialogProps {
  editor: LexicalEditor;
  open: boolean;
  onClose: () => void;
  selectionText?: string;
  selectionUrl?: string;
}

interface LinkDialogForm {
  text: string;
  link: string;
}

export const LinkDialog = ({
  editor,
  open,
  onClose,
  selectionText,
  selectionUrl,
}: LinkDialogProps) => {
  const [text, setText] = useState(selectionText || '');
  const [link, setLink] = useState(selectionUrl || '');
  const changed = text !== selectionText || link !== selectionUrl;
  const validator = useValidationService();

  const allValues = useMemo(() => {
    return {
      text,
      link,
    };
  }, [text, link]);

  const fieldValidators = useMemo(() => {
    return {
      text: [validator.required<LinkDialogForm, 'text'>('Обязательное поле')],
      link: selectionUrl
        ? []
        : [
            validator.required<LinkDialogForm, 'link'>('Обязательное поле'),
            validator.validUrl('Значение поля должно быть ссылкой'),
          ],
    };
  }, [validator, selectionUrl]);

  const {
    errors,
    touched,
    validateField,
    hasValidationErrors,
    showError,
    handleBlur,
  } = useForm<LinkDialogForm>(allValues, fieldValidators);

  const handleChangeTextInput = async (value?: string) => {
    setText(() => value || '');
    await validateField('text', value);
    showError('text');
  };

  const handleChangeLinkInput = async (value?: string) => {
    setLink(() => value || '');
    await validateField('link', value);
    showError('link');
  };

  const onSubmit = () => {
    editor.update(() => {
      const selection = $getSelection();

      if ($isRangeSelection(selection)) {
        selection.insertRawText(text);
      }

      editor.dispatchCommand(
        TOGGLE_LINK_COMMAND,
        link ? sanitizeUrl(link) : null,
      );
      onClose();
    });
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <form onSubmit={onSubmit}>
        <DialogContent
          title={selectionUrl ? 'Редактирование ссылки' : 'Добавление ссылки'}
          onClose={onClose}
          actions={
            <>
              <Button
                size="large"
                variant="tertiaryLight"
                type="button"
                onClick={onClose}
              >
                Отменить
              </Button>
              {selectionUrl ? (
                <Button
                  disabled={hasValidationErrors || !changed}
                  size="large"
                  variant="primary"
                  type="submit"
                >
                  Сохранить
                </Button>
              ) : (
                <Button
                  disabled={hasValidationErrors}
                  size="large"
                  variant="primary"
                  type="submit"
                >
                  Добавить
                </Button>
              )}
            </>
          }
        >
          <TextInput
            label="Текст ссылки"
            required
            placeholder="Введите текст"
            value={text}
            onChange={handleChangeTextInput}
            name="text"
            error={touched.text && !!errors.text}
            helperText={touched.text ? errors.text : undefined}
            onBlur={handleBlur('text')}
          />

          <TextInput
            label="Ссылка"
            required={!selectionUrl}
            placeholder="Адрес ссылки"
            value={link}
            onChange={handleChangeLinkInput}
            clearable
            name="link"
            error={touched.link && !!errors.link}
            helperText={touched.link ? errors.link : undefined}
            onBlur={handleBlur('link')}
          />
        </DialogContent>
      </form>
    </Dialog>
  );
};
